// PopupExpense.jsx
import React, { useState, useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import './popupExpense.css';

import PaperComponent from './PaperComponent';
import HeaderBar from './HeaderBar';
import ProjectSelect from './ProjectSelect';
import TaskSelect from './TaskSelect';
import CategorySelect from './CategorySelect';
import DateAndInvoiceRow from './DateAndInvoiceRow';
import AmountField from './AmountField';
import TaxRow from './TaxRow';
import NotesRow from './NotesRow';
import FileReupload from './FileReupload';
import ReceiptFillButton from './ReceiptFillButton';
import SubmitButton from './SubmitButton';
import EntitySelect from './EntitySelect';

import {
    formatTyping,
    toFixed2,
    parseNumeric,
    detectUS,
    formatDisplayDate,
    recomputeFromAmount, // VAT-correct
    recomputeFromNet,    // VAT-correct
} from './helpers';

const ALLOWED_TAXES = [0, 5.5, 6, 7, 10, 19, 20, 21];
const ENTITIES = [
    {
        "StaffID": 273386,
        "Name": "Novaspace SRL"
    },
    {
        "StaffID": 585401,
        "Name": "Les Services Novaspace Canada Inc."
    },
    {
        "StaffID": 406150,
        "Name": "Novaspace GmbH"
    },
    {
        "StaffID": 585402,
        "Name": "Novaspace SAS"
    },
    {
        "StaffID": 585404,
        "Name": "Novaspace Technology"
    },
    {
        "StaffID": 585405,
        "Name": "Novaspace Inc."
    },
    // india
    {
        "StaffID": 624601,
        "Name": "Novaspace India Private Limited"
    },
    // sydney
   {
        "StaffID": 625243,
        "Name": "Novaspace PTY LTD"
   },
   // japan
   {
        "StaffID": 625241,
        "Name": "Novaspace Japan"
   }
]

function PopupExpenseEdit({ setOpenPopup, candidates, openPopup, internalInvoice, notes, date, expenseid, fileContent, netAmount, taxAmount, taxRate, staffer }) {
    const isUS = useMemo(() => detectUS(), []);
    const [form, setForm] = useState({
        project: '',
        task: '',
        date: date ? String(date).slice(0, 10) : '', // yyyy-mm-dd
        category: '',
        expenseType: '',
        amount: netAmount && taxAmount ? toFixed2(Number(netAmount) + Number(taxAmount)) : 0.0, // gross
        taxRate: taxRate ? String(taxRate) + '%' : '',    // e.g., "19%"
        taxAmount: 0.0,
        netAmount: netAmount ? Number(netAmount) : 0.0,   // net 100%
        invoiceNumber: internalInvoice || '',
        notes: notes || '',
    });

    const [amountInput, setAmountInput] = useState(toFixed2(0)); // gross display
    const [netInput, setNetInput] = useState(toFixed2(0));       // net display
    const [lastEdited, setLastEdited] = useState('amount'); // 'amount' | 'net'
    const [file, setFile] = useState(fileContent || null); // File object
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
      if (!file) {
        setPreviewUrl(null);
        return;
      }
    
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    
      return () => URL.revokeObjectURL(url); // clean up
    }, [file]);
    const [loadingFill, setLoadingFill] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [stafferId, setStafferId] = useState(staffer || '');
    useEffect(() => {
        setFile(fileContent || null);
    }, [fileContent]);
    useEffect(() => {
        setForm((f) => {
            const next = { ...f };
            const netNum = netAmount ? Number(netAmount) : 0.0;
            const taxNum = taxAmount ? Number(taxAmount) : 0.0;
            next.netAmount = netNum;
            next.taxAmount = taxNum;
            next.amount = netNum + taxNum;
            next.taxRate = taxRate ? String(taxRate) + '%' : '';
            return next;
        });
    }, [taxAmount, netAmount, taxRate]);
    useEffect(() => {
        setStafferId(staffer || '');
    }, [staffer]);
    const [errors, setErrors] = useState({
        project: '',
        task: '',
        date: '',
        category: '',
        amount: '',
        taxRate: '',
        invoiceNumber: '',
        notes: '',
        file: '',
    });
    const [taxWarning, setTaxWarning] = useState('');

    // Snackbar / toast
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const openToast = (message, severity = 'success') =>
        setToast({ open: true, message, severity });
    const closeToast = () => setToast((t) => ({ ...t, open: false }));

    const displayDate = useMemo(() => formatDisplayDate(form.date, isUS), [form.date, isUS]);

    // Normalize candidates + sort
    const normalized = useMemo(() => {
        const safe = Array.isArray(candidates) ? candidates : [];
        const projects = safe.map((c) => {
            const projectId = c.projectId ?? c.ProjectID ?? c.project ?? c.ProjectName;
            const projectName = c.project ?? c.ProjectName ?? c.projectName ?? String(projectId ?? '');
            const rawTasks = Array.isArray(c.tasks) ? c.tasks : [];
            const tasks = rawTasks
                .map((t) =>
                    typeof t === 'string'
                        ? { id: t, name: t }
                        : { id: t.taskId ?? t.TaskID ?? t.id ?? t.name, name: t.task ?? t.TaskName ?? t.name }
                )
                .filter((t) => t.name && t.name.trim().toLowerCase() !== 'total')
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
            return { id: String(projectId ?? ''), name: projectName ?? '', tasks };
        });
        projects.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        return projects;
    }, [candidates]);

    const projectOptions = useMemo(
        () => normalized.map((p) => ({ id: p.id, name: p.name })),
        [normalized]
    );

    const tasksMap = useMemo(() => {
        const m = new Map();
        normalized.forEach((p) => m.set(String(p.id), p.tasks));
        return m;
    }, [normalized]);

    const taskOptions = tasksMap.get(String(form.project)) || [];

    const selectedProject = useMemo(
        () => projectOptions.find((p) => String(p.id) === String(form.project)) || null,
        [projectOptions, form.project]
    );
    const selectedTask = useMemo(
        () => taskOptions.find((t) => String(t.id) === String(form.task)) || null,
        [taskOptions, form.task]
    );

    const handleProjectSelect = (_e, option) => {
        setForm((f) => ({ ...f, project: option?.id ?? '', task: '' }));
        setErrors((err) => ({ ...err, project: '', task: '' }));
    };
    const handleTaskSelect = (_e, option) => {
        setForm((f) => ({ ...f, task: option?.id ?? '' }));
        setErrors((err) => ({ ...err, task: '' }));
    };

    // Recompute on tax rate change using VAT formulas
    useEffect(() => {
        setForm((f) => {
            let next = { ...f };
            if (lastEdited === 'amount') {
                next = { ...next, ...recomputeFromAmount(f.amount, f.taxRate) };
            } else {
                next = { ...next, ...recomputeFromNet(f.netAmount, f.taxRate) };
            }
            setAmountInput(toFixed2(next.amount));
            setNetInput(toFixed2(next.netAmount));
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.taxRate]);
    function isFloat(value) {
        // Regular expression to match a floating-point number
        const floatRegex = /^[+-]?\d+(\.\d+)?$/;
      
        return floatRegex.test(value);
      }
    // Typing handlers
    const onAmountTyping = (e) => {
        setLastEdited('amount');
        const rawValue = e.target.value.replace(/,/g, '');
        console.log('Raw amount input:', rawValue);
        const formattedValue = rawValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        console.log('Formatted amount input:', formattedValue);
        setAmountInput(formattedValue);
        //const numeric = parseNumeric(formattedValue);
        if(!isFloat(parseFloat(formattedValue.replace(/,/g, ''))))
            return;
        const numeric = parseFloat(formattedValue.replace(/,/g, ''));
        console.log('Numeric amount input:', numeric);
        setForm((f) => ({ ...f, ...recomputeFromAmount(numeric, f.taxRate) }));
        setErrors((err) => ({ ...err, amount: '' }));
      };

      const onNetTyping = (e) => {
        setLastEdited('net');
        /**
         *     const rawValue = e.target.value.replace(/,/g, ''); // Remove commas for processing
        const formattedValue = rawValue
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        setValue(formattedValue);
         */
        const rawValue = e.target.value.replace(/,/g, '');
        console.log('Raw net input:', rawValue);
        const formattedValue = rawValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        console.log('Formatted net input:', formattedValue);
        setNetInput(formattedValue);
        //const numeric = parseNumeric(formattedValue);
        if(!isFloat(parseFloat(formattedValue.replace(/,/g, ''))))
            return;
        const numeric = parseFloat(formattedValue.replace(/,/g, ''));
        setForm((f) => ({ ...f, ...recomputeFromNet(numeric, f.taxRate) }));
      };

    const onAmountBlur = () => setAmountInput(toFixed2(form.amount));
    const onNetBlur = () => setNetInput(toFixed2(form.netAmount));

    // Sync displays when numbers change externally
    useEffect(() => {
        setAmountInput(form.amount);
        setNetInput(form.netAmount);
      }, [form.amount, form.netAmount]);

    // Simple field updates
    const updateField = (name, value) => {
        setForm((f) => ({ ...f, [name]: value }));
        setErrors((err) => ({ ...err, [name]: '' }));
    };
    const handleCategoryChange = (value) => updateField('category', value);

    // Submit createExpense
    const handleSubmit = async (e) => {
        e.preventDefault();

        const nextErrors = {
            date: form.date ? '' : 'Date is required.',
            invoiceNumber: form.invoiceNumber ? '' : 'Invoice number is required.',
            notes: form.notes ? '' : 'Notes are required.',
            file: file ? '' : 'Receipt file is required.',
        };

        setErrors(nextErrors);
        const hasError = Object.values(nextErrors).some((msg) => !!msg);
        if (hasError || submitLoading) return;

        try {
            setSubmitLoading(true);

            const fd = new FormData();
            // required file
            fd.append('receipt', file);

            // core values
            fd.append('projectId', String(form.project));     // keep as string id
            fd.append('taskId', String(form.task));           // keep as string id
            fd.append('date', form.date);
            fd.append('category', form.category);

            // numeric fields as strings (backend can parseFloat)
            fd.append('amount', String(form.amount));         // gross
            fd.append('netAmount', String(form.netAmount));   // net 100%
            fd.append('taxAmount', String(form.taxAmount));
            const taxRateNum = form.taxRate && typeof form.taxRate === 'string' && form.taxRate.endsWith('%')
                ? Number(form.taxRate.slice(0, -1))
                : Number(form.taxRate);
            // taxRate as the string with % (e.g., "19%")
            fd.append('taxRate', String(taxRateNum));

            // other text fields
            fd.append('invoiceNumber', form.invoiceNumber);
            fd.append('notes', form.notes);
            console.log('Submitting expense:', {
                ...form
            })
            // ONLY FOR TESTING PURPOSES
            // if (Number(form.project) != 11164606 || form.task != 44622503) {
            //     console.warn('Overriding project/task for testing purposes');
            //     openToast('This functionality is in testing status. Only available for project: NVS-DEV Test project for developers ', 'warning');
            //     setSubmitLoading(false);
            //     return;
            // }



            const resp = await fetch('/api-dev/expenses/updateExpense/' + expenseid, {
                method: 'POST',
                body: fd,
            });

            // try to parse JSON (even for error)
            const maybeJson = await resp.json().catch(() => null);

            if (!resp.ok) {
                const msg =
                    (maybeJson && (maybeJson.error || maybeJson.message || maybeJson.detail)) ||
                    `Failed to create expense (HTTP ${resp.status})`;
                openToast("There was an error processing your request.", 'error');
                setSubmitLoading(false);
                return;
            }

            // success
            openToast('Expense updated successfully.', 'success');
            setSubmitLoading(false);
            setOpenPopup(false);
        } catch (err) {
            console.error('Create expense failed:', err);
            openToast('Failed to create expense. Please try again.', 'error');
            setSubmitLoading(false);
        }
    };

    // Category normalizer
    // const normalizeCategory = (cat) => {
    //     if (!cat) return '';
    //     const canon = [
    //         'Travel',
    //         'Other Direct Costs',
    //         'Sub-Contracting',
    //         'Reimbursable Expenses',
    //     ];
    //     const cleaned = String(cat).trim().toLowerCase();
    //     const found = canon.find(
    //         (c) => c.toLowerCase().replace(/\s+/g, ' ') === cleaned.replace(/\s+/g, ' ')
    //     );
    //     if (found) return found;
    //     if (/other\s*direct\s*costs/i.test(cat)) return 'Other Direct Costs';
    //     if (/reimbursable/i.test(cat)) return 'Reimbursable Expenses';
    //     if (/sub/i.test(cat)) return 'Sub-Contracting';
    //     if (/travel/i.test(cat)) return 'Travel';
    //     return '';
    // };

    // Fill from receipt (input_amount = NET base; amount = GROSS)
    // const handleFillForm = async () => {
    //     if (!file) {
    //         setErrors((err) => ({ ...err, file: 'Receipt file is required.' }));
    //         return;
    //     }
    //     setLoadingFill(true);
    //     setTaxWarning('');
    //     try {
    //         const formData = new FormData();
    //         formData.append('receipt', file);
    //         const resp = await fetch('/api-dev/expenses/fillExpenseFromReceipt', {
    //             method: 'POST',
    //             body: formData
    //         });
    //         if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
    //         const data = await resp.json();

    //         const iso = data.date ? String(data.date).slice(0, 10) : form.date;
    //         const normalizedCat = normalizeCategory(data.category) || form.category;

    //         // Project / Task — ensure string IDs everywhere
    //         let newProject = form.project;
    //         let newTask = form.task;

    //         if (data.projectId != null) {
    //             const pId = String(data.projectId);
    //             const projectExists = projectOptions.some((p) => String(p.id) === pId);
    //             if (projectExists) {
    //                 newProject = pId;

    //                 if (data.taskId != null) {
    //                     const tId = String(data.taskId);
    //                     const possibleTasks = tasksMap.get(pId) || [];
    //                     const taskExists = possibleTasks.some((t) => String(t.id) === tId);
    //                     newTask = taskExists ? tId : '';
    //                 } else {
    //                     newTask = '';
    //                 }
    //             }
    //         }

    //         // Tax handling
    //         let warning = '';
    //         let displayTaxRate = form.taxRate; // select value
    //         let calcTaxRateStr = form.taxRate; // used for calculation
    //         if (data.taxPercent != null && !Number.isNaN(Number(data.taxPercent))) {
    //             const rateNum = Number(data.taxPercent);
    //             calcTaxRateStr = `${rateNum}%`; // compute with detected rate
    //             if (ALLOWED_TAXES.includes(rateNum)) {
    //                 displayTaxRate = `${rateNum}%`;
    //             } else {
    //                 displayTaxRate = ''; // force user to pick
    //                 warning = `Detected tax ${rateNum}% doesn’t match available options. Please choose the correct rate.`;
    //             }
    //         }

    //         // Use NET from input_amount (base 100%)
    //         const newNet = typeof data.input_amount === 'number' ? data.input_amount : form.netAmount;

    //         // Compute VAT forward: amount = net + net*rate
    //         const recomputed = recomputeFromNet(newNet, calcTaxRateStr);

    //         setForm((f) => ({
    //             ...f,
    //             date: iso,
    //             category: normalizedCat,
    //             project: newProject,
    //             task: newTask,
    //             taxRate: displayTaxRate, // '' if non-standard
    //             ...recomputed,
    //             invoiceNumber: data.internal_invoice_number ?? f.invoiceNumber,
    //         }));

    //         setNetInput(toFixed2(recomputed.netAmount));
    //         setAmountInput(toFixed2(recomputed.amount));
    //         setLastEdited('net');
    //         setTaxWarning(warning);
    //     } catch (err) {
    //         console.error('Failed to fill form:', err);
    //         openToast('Failed to analyze receipt.', 'error');
    //     } finally {
    //         setLoadingFill(false);
    //     }
    // };

    return (
        <>
            <Dialog
                maxWidth="650px"
                fullWidth
                style={{ margin: "70px" }}
                open={openPopup}
                PaperComponent={PaperComponent}
                aria-labelledby="draggable-dialog-title"
            >
                <HeaderBar onClose={() => setOpenPopup(false)} editing={true} />
                <DialogContent>
                    <div className="popup-expense-container">
                        {/* LEFT SIDE: Form */}
                        <div className="form-section">
                            <form onSubmit={handleSubmit} noValidate>
                                <FileReupload
                                    file={file}
                                    onFileChange={(f) => {
                                        setFile(f);
                                        setErrors((err) => ({ ...err, file: '' }));
                                    }}
                                    onRemove={() => setFile(null)}
                                    errorText={errors.file}
                                    required
                                />

                                {/* <ReceiptFillButton show={!!file} onClick={handleFillForm} loading={loadingFill} /> */}
                                {/* <ProjectSelect
                                    options={projectOptions}
                                    value={selectedProject}
                                    onChange={handleProjectSelect}
                                    errorText={errors.project}
                                    disabled={true}
                                    required
                                />

                                <TaskSelect
                                    options={taskOptions}
                                    value={selectedTask}
                                    onChange={handleTaskSelect}
                                    disabled={true}
                                    errorText={errors.task}
                                    projectSelected={!!form.project}
                                    required
                                />

                                <CategorySelect
                                    value={form.category}
                                    onChange={handleCategoryChange}
                                    errorText={errors.category}
                                    disabled={true}
                                    required
                                /> */}
                                {/* <div className="form-input-expense" style={{ width: "100%" }}>
                                    <label className="form-input-label-expense">entityStafferz*</label>
                                    <input
                                        type="text"
                                        name="entity"
                                        className="form-input-field-expense disabled-input"
                                        value={ENTITIES.find(ent => ent.StaffID === entityStaffer)?.Name || ''}
                                        disabled
                                    />
                                </div> */}
                                <div className="form-input-expense">
                                    <label className="form-input-label-expense">Entity*</label>
                                    <input
                                        type="text"
                                        name="entity"
                                        className="form-input-field-expense disabled-input"
                                        placeholder="Entity"
                                        value={ENTITIES.find(ent => ent.StaffID === Number(stafferId))?.Name || ''}
                                        disabled
                                    />
                                </div>

                                <DateAndInvoiceRow
                                    value={form.date}
                                    onChange={(v) => updateField('date', v)}
                                    errorText={errors.date}
                                    invoiceNumber={form.invoiceNumber}
                                    onInvoiceChange={(v) => updateField('invoiceNumber', v)}
                                    invoiceErrorText={errors.invoiceNumber}
                                    required
                                />

                                <TaxRow
                                    taxRate={form.taxRate}
                                    onTaxRateChange={(v) => {
                                        setTaxWarning('');
                                        updateField('taxRate', v);
                                    }}
                                    taxAmountFormatted={toFixed2(form.taxAmount)}
                                    netInput={netInput}
                                    onNetTyping={onNetTyping}
                                    onNetBlur={onNetBlur}
                                    onNetFocus={() => setLastEdited('net')}
                                    errorText={errors.taxRate}
                                    warningText={taxWarning}
                                    value={amountInput}
                                    onChange={onAmountTyping}
                                    onBlur={onAmountBlur}
                                    onFocus={() => setLastEdited('amount')}
                                    errorTextAmount={errors.amount}
                                    required
                                    includeZeroOption
                                />

                                <NotesRow
                                    notes={form.notes}
                                    onNotesChange={(v) => updateField('notes', v)}
                                    notesErrorText={errors.notes}
                                    required
                                />



                                <SubmitButton disabled={submitLoading || loadingFill} loading={submitLoading} />
                            </form>
                        </div>

                        {/* RIGHT SIDE: Receipt preview */}
                        {file && (
                            <div className="receipt-preview-container">
                                <p className="preview-title">Receipt Preview</p>
                                {file.type.startsWith('image/') ? (
                                    <img
                                        src={previewUrl}
                                        alt="Receipt preview"
                                        className="receipt-preview-image"
                                    />
                                ) : file.type === 'application/pdf' ? (
                                    <iframe
                                        src={previewUrl}
                                        title="Receipt PDF Preview"
                                        className="receipt-preview-pdf"
                                        style={{ height: "350px" }}
                                    />
                                ) : (
                                    <p className="preview-unsupported">Preview not available for this file type.</p>
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>

            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={closeToast}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                style={{ marginTop: '60px', zIndex: 1300 }}

            >
                <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default PopupExpenseEdit;
